import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = user
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("sc_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("sc_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sc_token", data.token);
      localStorage.removeItem("sc_admin_token");
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const register = async () => ({ ok: false, error: "Self sign-up is disabled. Contact your administrator." });

  const changePassword = async (current_password, new_password) => {
    try {
      await api.post("/auth/change-password", { current_password, new_password });
      await loadMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const impersonate = async (userId) => {
    try {
      const { data } = await api.post(`/auth/impersonate/${userId}`);
      if (!localStorage.getItem("sc_admin_token")) localStorage.setItem("sc_admin_token", localStorage.getItem("sc_token"));
      localStorage.setItem("sc_token", data.token);
      window.location.href = "/";
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const exitImpersonation = () => {
    const orig = localStorage.getItem("sc_admin_token");
    localStorage.removeItem("sc_admin_token");
    if (orig) localStorage.setItem("sc_token", orig);
    else localStorage.removeItem("sc_token");
    window.location.href = "/";
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout endpoint failed (proceeding with local logout):", e); }
    localStorage.removeItem("sc_token");
    localStorage.removeItem("sc_admin_token");
    setUser(false);
    window.location.href = "/login";
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, changePassword, logout, setUser, refreshUser, impersonate, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
